/**
 * Per Ankh — keep a page up to date the moment something happens.
 *
 * Asks the server "tell me when something changes for me" (GET /api/member/live) and
 * sits waiting for the answer. The server replies within ~12 seconds either way, and
 * the moment there is news; the page then runs `onChange` to re-read what it shows,
 * and asks again. See server/lib/live.js for the other half.
 *
 * What it does for the member:
 *   - nothing is polled while the tab is hidden; coming back re-reads straight away,
 *   - a dropped connection or an error backs off (2s, 4s, … up to 30s) and recovers by
 *     itself, re-reading once it is back so nothing missed is left missed,
 *   - one loop per page — other code on the page listens for the `pa:live` event the
 *     caller dispatches, rather than starting its own.
 */

export const RETRY_BASE_MS = 2000;
export const RETRY_MAX_MS = 30000;
/** A reply this fast with nothing new is not a wait; pause so we never spin. */
export const TOO_QUICK_MS = 400;
export const QUICK_PAUSE_MS = 1500;
/** Give up on a request that has hung well past the server's own limit. */
export const REQUEST_TIMEOUT_MS = 35000;

/** How long to wait before retrying after `failures` errors in a row (1, 2, 3, …). */
export function retryDelay(failures) {
  if (!(failures > 0)) return 0;
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1));
}

/**
 * @param {object} opts
 * @param {() => Promise<object>} [opts.headers]  extra request headers (the sign-in token)
 * @param {() => void} opts.onChange              something changed — re-read what you show
 * @param {string} [opts.url]
 * @returns {{ stop: () => void }}
 */
export function startLiveUpdates({ headers = async () => ({}), onChange, url = '/api/member/live' } = {}) {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return { stop() {} };

  let stopped = false;
  let version = null;            // the last counter the server gave us
  let failures = 0;
  let inflight = null;           // AbortController of the request in flight
  let wake = null;               // resolves a pause early (tab shown again)

  const sleep = (ms) => new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    wake = () => { clearTimeout(t); resolve(); };
  });

  async function loop() {
    while (!stopped) {
      if (document.hidden) {                        // nobody is looking: wait for the tab to return
        await new Promise((resolve) => { wake = resolve; });
        if (stopped) return;
        safely(onChange);                           // catch up on whatever happened meanwhile
        version = null;
        continue;
      }

      const began = Date.now();
      inflight = new AbortController();
      const killer = setTimeout(() => inflight.abort(), REQUEST_TIMEOUT_MS);
      try {
        const query = version === null ? '' : `?since=${version}`;
        const res = await fetch(`${url}${query}`, { headers: await headers(), credentials: 'include', signal: inflight.signal, cache: 'no-store' });
        if (!res.ok) throw new Error(`live ${res.status}`);
        const body = await res.json();
        const recovering = failures > 0;
        failures = 0;
        const firstAnswer = version === null;
        version = Number.isInteger(body.v) ? body.v : null;
        if ((body.changed && !firstAnswer) || recovering) safely(onChange);
        if (!body.changed && Date.now() - began < TOO_QUICK_MS) await sleep(QUICK_PAUSE_MS);
      } catch (err) {
        if (stopped) return;
        if (err?.name === 'AbortError' && document.hidden) continue;   // we cancelled it ourselves
        failures += 1;
        version = null;     // ask afresh next time: the server answers at once, and we re-read straight away
        await sleep(retryDelay(failures));
      } finally {
        clearTimeout(killer);
        inflight = null;
      }
    }
  }

  function safely(fn) { try { fn(); } catch (err) { console.error('[live]', err); } }

  function onVisibility() {
    if (document.hidden) inflight?.abort();         // stop holding a request open for a hidden tab
    else wake?.();
  }
  document.addEventListener('visibilitychange', onVisibility);

  loop();
  return {
    stop() {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      inflight?.abort();
      wake?.();
    },
  };
}
